import React, { useMemo, useState } from 'react';
import { Modal, Tabs, Stack, Group, Text, Button, ActionIcon, Badge, Popover, Autocomplete, Select, NumberInput, Tooltip } from '@mantine/core';
import { IconUsers, IconUserPlus, IconTrash, IconPlus, IconX, IconCrown, IconCrownOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export default function ManageMembersModal({
  opened,
  onClose,
  isOwner = false,
  owners = [],
  participants = [],
  onRemove,
  requests = [],
  onApprove,
  onReject,
  atMemberCap = false,
  roleSuggestions = [],
  onAddRole,
  onRemoveRole,
  onAddGhost,
  onSetContract,
  onSetWeeklyHours,
  onMakeOwner,
  onRemoveOwner,
}) {
  const { t } = useTranslation('common');
  const [addingForUid, setAddingForUid] = useState(null);
  // Local edit buffer for weekly hours to avoid flicker and commit on blur
  const [weeklyLocal, setWeeklyLocal] = useState({}); // uid -> number|string
  const pending = Array.isArray(requests)
    ? requests.filter((r) => (r.status || 'pending') === 'pending')
    : [];

  const suggestions = useMemo(() => (Array.isArray(roleSuggestions) ? roleSuggestions : []), [roleSuggestions]);
  const [newRoleValue, setNewRoleValue] = useState('');
  const [ghostName, setGhostName] = useState('');
  const [ghostContract, setGhostContract] = useState('internal');

  // Sync local weekly values from participants when list changes
  React.useEffect(() => {
    setWeeklyLocal((prev) => {
      const next = {};
      const present = new Set();
      if (Array.isArray(participants)) {
        for (const u of participants) {
          const uid = u.uid || u.value;
          present.add(uid);
          // Preserve local edit if exists; otherwise seed from incoming data
          if (Object.prototype.hasOwnProperty.call(prev, uid)) {
            next[uid] = prev[uid];
          } else {
            next[uid] = (u.weeklyHoursMax ?? '');
          }
        }
      }
      return next;
    });
  }, [participants]);

  // Flush pending weekly hours edits when modal closes
  React.useEffect(() => {
    if (!opened) {
      (async () => {
        if (!isOwner || typeof onSetWeeklyHours !== 'function') return;
        const partArray = Array.isArray(participants) ? participants : [];
        for (const u of partArray) {
          const uid = u.uid || u.value;
          if (!uid) continue;
          const raw = weeklyLocal[uid];
          // Only attempt if user touched it
          if (raw === undefined) continue;
          const srv = u.weeklyHoursMax ?? '';
          // If equal as string, skip
          if (String(raw) === String(srv)) continue;
          const num = Number(raw);
          if (!Number.isFinite(num) || num <= 0) {
            await onSetWeeklyHours(uid, null);
          } else {
            const n = Math.max(1, Math.min(168, Math.round(num)));
            await onSetWeeklyHours(uid, n);
          }
        }
      })();
    }
  }, [opened, isOwner, onSetWeeklyHours, participants, weeklyLocal]);

  return (
    <Modal opened={opened} onClose={onClose} title={t('people.title')}>
      <Tabs defaultValue="members">
        <Tabs.List>
          <Tabs.Tab value="members" leftSection={<IconUsers size={14} />}>{t('people.tabs.members')}</Tabs.Tab>
          {isOwner && (
            <Tabs.Tab value="requests" leftSection={<IconUserPlus size={14} />}>
              {t('people.tabs.requests')}{pending.length ? <Badge size="xs" ml={6}>{pending.length}</Badge> : null}
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="members" pt="sm">
          <Stack>
            {isOwner && (
              <Group>
                <Autocomplete
                  placeholder={t('people.add.placeholder')}
                  data={[]}
                  value={ghostName}
                  onChange={(v) => setGhostName(v)}
                  style={{ flex: 1 }}
                />
                <Select
                  label={null}
                  placeholder={t('people.add.contract')}
                  data={[{ value: 'internal', label: t('people.add.contract_internal') }, { value: 'external', label: t('people.add.contract_external') }]}
                  value={ghostContract}
                  onChange={(v) => setGhostContract(v || 'internal')}
                  style={{ width: 140 }}
                />
                <Button leftSection={<IconUserPlus size={14} />} disabled={atMemberCap} onClick={async () => {
                  const name = (ghostName || '').trim();
                  if (!name) return;
                  try {
                    await onAddGhost?.({ displayName: name, contract: ghostContract });
                    setGhostName('');
                    setGhostContract('internal');
                  } catch {}
                }}>
                  {atMemberCap ? t('people.add.plan_limit') : t('people.add.button')}
                </Button>
              </Group>
            )}
            {Array.isArray(participants) && participants.length ? (
              participants.map((u) => {
                const uid = u.uid || u.value;
                const roles = Array.isArray(u.roles) ? u.roles : [];
                const isOwnerUser = Array.isArray(owners) ? owners.includes(uid) : false;
                const isGhost = !u.email;
                return (
                  <Stack key={uid} gap={6} p={8} style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
                    <Group justify="space-between" align="center" wrap="wrap">
                      <Group gap={8} align="center">
                        <Text fw={600}>{u.displayName || u.email || u.label || uid}</Text>
                        {isOwnerUser && (
                          <Badge size="sm" color="green" variant="light">{t('dashboard.owner_badge')}</Badge>
                        )}
                      </Group>
                      <Group gap="xs" align="center">
                        {isOwner && isOwnerUser && onRemoveOwner && (Array.isArray(owners) && owners.length > 1) && (
                          <Tooltip label={t('people.remove_owner', { defaultValue: 'Remove owner' })}>
                            <ActionIcon color="orange" variant="subtle" onClick={() => onRemoveOwner(uid)} aria-label={t('people.remove_owner', { defaultValue: 'Remove owner' })}>
                              <IconCrownOff size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {isOwner && !isOwnerUser && !isGhost && onMakeOwner && (
                          <Tooltip label={t('people.make_owner', { defaultValue: 'Make owner' })}>
                            <ActionIcon color="green" variant="subtle" onClick={() => onMakeOwner(uid)} aria-label={t('people.make_owner', { defaultValue: 'Make owner' })}>
                              <IconCrown size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Select
                          aria-label={t('people.member.contract_label_aria')}
                          data={[{ value: 'internal', label: t('people.add.contract_internal') }, { value: 'external', label: t('people.add.contract_external') }]}
                          value={u.contract || 'internal'}
                          onChange={(v) => onSetContract && onSetContract(uid, v || 'internal')}
                          size="xs"
                          w={140}
                          disabled={!isOwner}
                        />
                        <NumberInput
                          aria-label={t('people.member.weekly_hours_aria')}
                          placeholder={t('people.member.weekly_hours_placeholder')}
                          value={weeklyLocal[uid] ?? ''}
                          onChange={(val) => setWeeklyLocal((prev) => ({ ...prev, [uid]: val }))}
                          onBlur={async () => {
                            if (!onSetWeeklyHours) return;
                            const raw = weeklyLocal[uid];
                            const num = Number(raw);
                            if (!Number.isFinite(num) || num <= 0) {
                              // Unset personalized cap
                              const ok = await onSetWeeklyHours(uid, null);
                              if (ok) {
                                setWeeklyLocal((prev) => ({ ...prev, [uid]: '' }));
                              } else {
                                // Revert to server value
                                const srv = u.weeklyHoursMax ?? '';
                                setWeeklyLocal((prev) => ({ ...prev, [uid]: srv }));
                              }
                            } else {
                              const n = Math.max(1, Math.min(168, Math.round(num)));
                              const ok = await onSetWeeklyHours(uid, n);
                              if (ok) {
                                setWeeklyLocal((prev) => ({ ...prev, [uid]: n }));
                              } else {
                                const srv = u.weeklyHoursMax ?? '';
                                setWeeklyLocal((prev) => ({ ...prev, [uid]: srv }));
                              }
                            }
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const raw = weeklyLocal[uid];
                              const num = Number(raw);
                              if (!Number.isFinite(num) || num <= 0) {
                                const ok = await onSetWeeklyHours(uid, null);
                                if (ok) setWeeklyLocal((prev) => ({ ...prev, [uid]: '' }));
                              } else {
                                const n = Math.max(1, Math.min(168, Math.round(num)));
                                const ok = await onSetWeeklyHours(uid, n);
                                if (ok) setWeeklyLocal((prev) => ({ ...prev, [uid]: n }));
                              }
                            }
                          }}
                          min={0}
                          max={168}
                          step={1}
                          size="xs"
                          w={120}
                          disabled={!isOwner}
                          hideControls
                        />
                        {isOwner && onRemove && (
                          <ActionIcon color="red" variant="subtle" onClick={() => onRemove(uid)} title={t('people.member.remove_title')}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Group>
                    <Group gap="xs" wrap="wrap">
                      {roles.map((role) => (
                        <Badge key={role} variant="light" size="sm" rightSection={isOwner && onRemoveRole ? (
                          <ActionIcon size="xs" variant="subtle" color="gray" aria-label={t('people.roles.remove_aria')} onClick={() => onRemoveRole(uid, role)}>
                            <IconX size={12} />
                          </ActionIcon>
                        ) : null}>
                          {role}
                        </Badge>
                      ))}
                      {isOwner && onAddRole && (
                        <Popover opened={addingForUid === uid} onChange={(open) => !open && setAddingForUid(null)} withArrow>
                          <Popover.Target>
                            <ActionIcon size="sm" variant="light" color="blue" onClick={() => setAddingForUid(uid)} aria-label={t('people.roles.add_title')} title={t('people.roles.add_title')}>
                              <IconPlus size={14} />
                            </ActionIcon>
                          </Popover.Target>
                          <Popover.Dropdown>
                            <Autocomplete
                              data={suggestions}
                              placeholder={t('people.roles.type_or_select')}
                              value={newRoleValue}
                              onChange={setNewRoleValue}
                              onOptionSubmit={async (val) => {
                                const v = String(val ?? newRoleValue ?? '').trim();
                                if (!v) return;
                                try {
                                  await onAddRole(uid, v);
                                } finally {
                                  setNewRoleValue('');
                                  setAddingForUid(null);
                                }
                              }}
                              autoFocus
                            />
                          </Popover.Dropdown>
                        </Popover>
                      )}
                      {roles.length === 0 && <Text size="sm" c="dimmed">{t('people.roles.none')}</Text>}
                    </Group>
                  </Stack>
                );
              })
            ) : (
              <Text c="dimmed">{t('people.none')}</Text>
            )}
          </Stack>
        </Tabs.Panel>

        {isOwner && (
          <Tabs.Panel value="requests" pt="sm">
            <Stack>
              {pending.length === 0 ? (
                <Text c="dimmed">{t('people.requests.none')}</Text>
              ) : (
                pending.map((r) => (
                  <Group key={r.uid} justify="space-between" align="center">
                    <Stack gap={0}>
                      <Text fw={600}>{r.displayName || r.email || r.uid}</Text>
                      {r.email && <Text size="sm" c="dimmed">{r.email}</Text>}
                    </Stack>
                    <Group gap="xs">
                      <Button size="xs" variant="light" color="green" onClick={() => onApprove && onApprove(r.uid)}>{t('people.requests.approve')}</Button>
                      <Button size="xs" variant="light" color="red" onClick={() => onReject && onReject(r.uid)}>{t('people.requests.reject')}</Button>
                    </Group>
                  </Group>
                ))
              )}
            </Stack>
          </Tabs.Panel>
        )}
      </Tabs>
    </Modal>
  );
}
